import { Box, Typography, useTheme } from "@mui/material";

import { DataChunk } from "src/views/assistant/components/sources/SourceListItem";
import DocumentDataCard from "../components/DocumentDataCard";
import { Loading } from "src/reusable/LoadingSpinner";
import LoadingOverlay from "react-loading-overlay-ts";
import { Search as SearchIcon } from "@mui/icons-material";
import { SourceDataRecord } from "../types";
import { useHistory } from "react-router-dom";

type DocumentDatasetSearchProps = {
  datasetId: number;
  searchText: string;
  documentData: Array<{
    dataChunks: DataChunk[];
    sourceDataRecords: SourceDataRecord[];
  }>;
  isFetchingSourceDataRecords: boolean;
  isSourceDataRecordsLoading: boolean;
};

const DocumentDatasetSearch = ({
  datasetId,
  searchText,
  documentData,
  isFetchingSourceDataRecords,
  isSourceDataRecordsLoading,
}: DocumentDatasetSearchProps) => {
  const history = useHistory();
  const theme = useTheme();

  const totalResults = documentData.reduce(
    (acc, data) =>
      acc + Math.min(data.dataChunks.length, data.sourceDataRecords.length),
    0,
  );

  return (
    <LoadingOverlay
      active={isFetchingSourceDataRecords || isSourceDataRecordsLoading}
      spinner={<Loading height={"75em"} width={"75em"} />}
      text="Loading Search Results..."
    >
      {searchText &&
        searchText !== "" &&
        !isFetchingSourceDataRecords &&
        !isSourceDataRecordsLoading && (
          <Box
            sx={{
              p: 2,
              backgroundColor: theme.palette.background.default,
            }}
          >
            {/* Search Results Header */}
            <Box
              sx={{
                mb: 3,
                p: 2,
                backgroundColor: theme.palette.background.paper,
                borderRadius: "8px",
                border: `1px solid ${theme.palette.divider}`,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <SearchIcon
                sx={{
                  color: theme.palette.primary.main,
                  fontSize: "1.5rem",
                }}
              />
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                  }}
                >
                  Search Results
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.875rem",
                  }}
                >
                  Found {totalResults}{" "}
                  {totalResults === 1 ? "result" : "results"} for "{searchText}"
                </Typography>
              </Box>
            </Box>

            {/* Results List */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {documentData.flatMap((data, documentIndex) => {
                const maxLength = Math.max(
                  data.dataChunks.length,
                  data.sourceDataRecords.length,
                );

                return Array.from({ length: maxLength }, (_, index) => {
                  const dataChunk = data.dataChunks[index];
                  const sourceDataRecord = data.sourceDataRecords[index];

                  if (!dataChunk || !sourceDataRecord) {
                    return null;
                  }

                  return (
                    <DocumentDataCard
                      key={`${documentIndex}-${index}`}
                      type="search"
                      searchText={searchText}
                      datasetId={datasetId}
                      pageNumberCallback={(pageNum) => {
                        history.push(
                          `/datasets/${datasetId}/document/${encodeURIComponent(
                            sourceDataRecord.id,
                          )}?page_number=${pageNum}&cid=${
                            dataChunk.id
                          }&search=${encodeURIComponent(searchText)}`,
                        );
                      }}
                      // @ts-ignore
                      chunk_data={dataChunk.chunk_data}
                      chunk_metadata={dataChunk.chunk_metadata}
                      chunk_create_date={new Date().toISOString()}
                      chunk_id={dataChunk.id}
                      sdr_id={sourceDataRecord.id}
                      sdr_create_date={sourceDataRecord.create_date}
                      sdr_data={sourceDataRecord.data}
                      __source_uid={sourceDataRecord.source_uid}
                      scrollAndView={false}
                      index={index + 1}
                    />
                  );
                });
              })}
            </Box>
          </Box>
        )}
    </LoadingOverlay>
  );
};

export default DocumentDatasetSearch;
